// client/src/coms/settings/sections/ConnectionSettings.js
// Personal Settings → Connections & Integrations: Steam (game matching), your cloud storage accounts (managed
// with the same dialog as Files), and Spotify for Watch Together.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Section } from '../../personalisation/settingsControls';
import ConnectStorageDialog from '../../organisation/ConnectStorageDialog';
import { AccountCard } from '../../organisation/storageAccounts';
import { apiGetJson } from '../../organisation/orgApi';
import { getProviderSession } from '../../auth/providerSessions';

export function extractSteamAccountCode(input) {
  const raw = String(input || '').trim();
  if (!raw) return '';
  const cleaned = raw.replace(/[?#].*$/, '').trim();
  const bare = (v) => {
    const s = String(v || '').trim().replace(/^\/+|\/+$/g, '');
    if (/^\d{17}$/.test(s) || /^[A-Za-z0-9_-]{2,64}$/.test(s)) return s;
    return null;
  };
  if (/^\d{17}$/.test(cleaned) || /^[A-Za-z0-9_-]{2,64}$/.test(cleaned)) return cleaned;
  const m = cleaned.match(/(?:^|\/)(profiles|id)\/([^/?#]+)/i);
  if (m) {
    const code = bare(m[2]);
    if (m[1].toLowerCase() === 'profiles') return code && /^\d{17}$/.test(code) ? code : null;
    return code;
  }
  return null;
}

async function steamApi(body) {
  const res = await fetch('/api/coms/me/steam', {
    method: body ? 'PUT' : 'GET',
    credentials: 'include',
    cache: 'no-store',
    headers: body ? { Accept: 'application/json', 'Content-Type': 'application/json' } : { Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || !data || !data.ok) throw new Error((data && data.error) || `Could not reach the server (${res.status}).`);
  return String(data.steam_id || '').trim();
}

function SteamRow() {
  const [saved, setSaved] = useState('');
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    steamApi()
      .then((v) => !cancelled && (setSaved(v), setInput(v)))
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const code = useMemo(() => extractSteamAccountCode(input), [input]);
  const dirty = input.trim() !== saved;

  const save = async (value) => {
    setError('');
    setMessage('');
    setBusy(true);
    try {
      const v = await steamApi({ steam_id: value || null });
      setSaved(v);
      setInput(v);
      setMessage(v ? 'Steam account saved.' : 'Steam account removed.');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Row
      label="Steam account"
      hint="Used to suggest games you and your friends own. Paste your Steam profile link or account code; only the public profile code is kept, and your Steam profile must be public."
      settingId="steam"
      scope="account"
      badge={dirty ? <span className="vx-unsaved">Not saved</span> : null}
    >
      <form
        className="vx-inline"
        onSubmit={(e) => {
          e.preventDefault();
          if (code !== null && dirty) save(code);
        }}
      >
        <input
          type="text"
          value={input}
          disabled={loading || busy}
          onChange={(e) => {
            setError('');
            setMessage('');
            setInput(e.target.value);
          }}
          placeholder="steamcommunity.com/id/… or account code"
          aria-label="Steam profile link or account code"
          autoComplete="off"
          spellCheck={false}
          className="vx-text-input"
          data-testid="settings-steam-input"
        />
        <button type="submit" className="vx-btn small primary" disabled={loading || busy || !dirty || code === null}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        {saved ? (
          <button type="button" className="vx-btn small" disabled={busy} onClick={() => save('')}>
            Remove
          </button>
        ) : null}
      </form>
      {input.trim() && code === null ? <div className="vx-acct-error">That is not a Steam profile link or account code.</div> : null}
      {input.trim() && code && dirty ? <div className="vx-set-hint">Will be saved as {code}.</div> : null}
      {error ? (
        <div className="vx-acct-error" role="alert">
          {error}
        </div>
      ) : null}
      {message ? (
        <div className="vx-set-hint" role="status">
          {message}
        </div>
      ) : null}
    </Row>
  );
}

function StorageRow() {
  const [conns, setConns] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const r = await apiGetJson('/me/storage/connections');
      setConns(r.connections || []);
    } catch (e) {
      setConns([]);
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Row
      label="Cloud storage"
      hint="Accounts you connected to use your own files in Voxemera (Google Drive today; other providers are coming). Voxemera only sees the files you choose, and never copies large files to its own storage."
      settingId="storage"
      scope="account"
    >
      <div className="vx-conn-list">
        {conns === null ? <span className="vx-set-hint">Loading…</span> : null}
        {conns && !conns.length && !error ? <span className="vx-set-hint">No accounts connected.</span> : null}
        {(conns || []).map((c) => (
          <AccountCard key={c.id} conn={c} compact />
        ))}
        <button type="button" className="vx-btn small" onClick={() => setOpen(true)} data-testid="settings-manage-storage">
          {conns && conns.length ? 'Manage accounts' : 'Connect an account'}
        </button>
      </div>
      {error ? (
        <div className="vx-acct-error" role="alert">
          {error}
        </div>
      ) : null}
      {open ? (
        <ConnectStorageDialog
          base=""
          serverKey=""
          canManage={false}
          onClose={() => {
            setOpen(false);
            load();
          }}
          onConnected={() => {
            setOpen(false);
            load();
          }}
        />
      ) : null}
    </Row>
  );
}

export default function ConnectionSettings() {
  const spotify = getProviderSession('spotify');
  const spotifyName = spotify && spotify.profile ? spotify.profile.display_name || spotify.profile.id : '';
  return (
    <Section id="vx-cat-connections" title="Connections & Integrations" description="Other services you link to Voxemera. Voxemera never asks for their passwords: you sign in on their own page.">
      <SteamRow />
      <StorageRow />
      <Row
        label="Spotify"
        hint="Connect Spotify inside Watch Together to play music together. The connection is kept in this browser."
        settingId="spotify"
        scope="device"
      >
        <span className={`vx-set-value${spotify ? '' : ' is-muted'}`}>{spotify ? `Connected${spotifyName ? ` as ${spotifyName}` : ''}` : 'Not connected'}</span>
      </Row>
    </Section>
  );
}
